/**
 * WKLEJENIE TABELI ZE SCHOWKA — czysta reguła, bez Reacta i bez DOM-u.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZGŁOSZONE 07.09.2026: „jak kopiuję tabelkę z maila i wklejam, to tekst jest w takim
 *  formacie" — i dalej wklejka, w której KAŻDA KOMÓRKA stoi w osobnej linijce, a między nimi
 *  siedzą linijki z samym tabulatorem. Okno importu widziało wtedy 20 wierszy po jednym polu
 *  zamiast 4 wierszy po 5 pól.
 *
 *  ⚠️⚠️ PRZYCZYNA, I ONA WSKAZUJE ROZWIĄZANIE: schowek ma DWIE postacie tej samej rzeczy.
 *  `text/html` to prawdziwa tabela (`<tr>`/`<td>`), `text/plain` to jej rozpisanie na linijki
 *  zrobione przez klienta pocztowego. Excel bierze HTML i dlatego „wkleiłem do excela, a potem
 *  na stronę i było ok"; `<textarea>` dostaje sam `text/plain`. Bierzemy więc to samo, co Excel.
 *
 *  ⚠️⚠️ HTML-A NIGDZIE NIE WSTAWIAMY — ani do DOM-u, ani przez `innerHTML`. Skanujemy NAPIS
 *  i wyciągamy z niego tekst komórek; wynikiem jest zwykły tekst rozdzielony tabulatorami, który
 *  ląduje w polu tekstowym. Żaden fragment schowka nie staje się elementem strony, więc nie ma
 *  tu powierzchni na wykonanie czegokolwiek.
 *
 *  ⚠️ SKANER, NIE JEDEN SPRYTNY WZORZEC. Kolejność jest wprost: tabela → wiersze → komórki →
 *  tekst. Jeden regex na całość jest tym błędem, po którym parser HTML-a przestaje działać przy
 *  pierwszym atrybucie ze znakiem `>`.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Jednostki HTML, które realnie przychodzą ze schowka klienta pocztowego. */
const JEDNOSTKI: Readonly<Record<string, string>> = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  '#39': "'",
};

/**
 * KONIEC ZNACZNIKA ZACZYNAJĄCEGO SIĘ NA `od` — z pominięciem `>` W CUDZYSŁOWACH.
 *
 * ═══════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ISTNIEJE, BO `[^>]*>` TU NIE WYSTARCZA — i sprawdził to test, nie przewidywanie.
 *  Pierwsza wersja tego pliku miała w komentarzu, że „jeden sprytny wzorzec przestaje działać
 *  przy pierwszym atrybucie ze znakiem `>`", i JEDNOCZEŚNIE używała takiego wzorca: przy
 *  `<td title="a>b">X</td>` komórka zaczynała się od `b">X`. Komentarz obiecywał odporność,
 *  której kod nie miał.
 *
 *  ⚠️ NIEDOMKNIĘTY ZNACZNIK KOŃCZY SIĘ KOŃCEM NAPISU. Schowek bywa ucięty; lepiej wziąć resztę
 *  jako jeden znacznik niż zapętlić się albo zgubić całą tabelę.
 * ═══════════════════════════════════════════════════════════════
 */
export function koniecZnacznika(html: string, od: number): number {
  let cudzyslow: string | null = null;
  for (let i = od + 1; i < html.length; i += 1) {
    const z = html[i]!;
    if (cudzyslow !== null) {
      if (z === cudzyslow) cudzyslow = null;
      continue;
    }
    if (z === '"' || z === "'") {
      cudzyslow = z;
      continue;
    }
    if (z === '>') return i + 1;
  }
  return html.length;
}

/** Zdjęcie wszystkich znaczników — tym samym skanerem, więc z tą samą odpornością na `>` w atrybucie. */
export function bezZnacznikow(html: string): string {
  let out = '';
  for (let i = 0; i < html.length; ) {
    if (html[i] === '<') {
      i = koniecZnacznika(html, i);
      continue;
    }
    const nast = html.indexOf('<', i);
    out += nast === -1 ? html.slice(i) : html.slice(i, nast);
    i = nast === -1 ? html.length : nast;
  }
  return out;
}

/**
 * Rozkodowanie jednostek i zwinięcie białych znaków w komórce.
 *
 * ⚠️ `&nbsp;` NA SPACJĘ, NIE NA ZNAK NIEROZDZIELAJĄCY (U+00A0). Ten drugi przechodzi przez
 * `trim()` NIETKNIĘTY, więc numer karty „ 8BFF4E32" zostawał ze spacją na początku — a numer
 * karty jest kluczem naturalnym w czterech tabelach SPS i taka spacja tworzy inny klucz.
 */
export function tekstKomorkiHtml(fragment: string): string {
  /** `<br>` w komórce to nadal TA SAMA komórka — dwie linijki adresu nie mogą rozerwać wiersza. */
  const bezLamania = fragment.replace(/<br\s*\/?>/gi, ' ');
  /** ⚠️ Znaczniki zdejmuje SKANER, nie wzorzec `[^>]*` — patrz `koniecZnacznika`. */
  return bezZnacznikow(bezLamania)
    .replace(/&#x([0-9a-f]+);/gi, (_, h: string) => String.fromCodePoint(Number.parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d: string) => String.fromCodePoint(Number.parseInt(d, 10)))
    .replace(/&([a-z#0-9]+);/gi, (całość, nazwa: string) => JEDNOSTKI[nazwa.toLowerCase()] ?? całość)
    .replace(/ /g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Wycinki między znacznikami otwierającymi danego rodzaju. `null` = nie ma ani jednego. */
function wytnij(html: string, znaczniki: readonly string[]): string[] | null {
  /**
   * ⚠️ WZORZEC SZUKA WYŁĄCZNIE POCZĄTKU ZNACZNIKA, a jego koniec wyznacza skaner — bo tylko
   * on wie, że `>` w cudzysłowie nie kończy znacznika. Wzorzec dopasowujący całość (`[^>]*>`)
   * ucinał komórkę przy `<td title="a>b">`, co wyszło w teście.
   */
  const wzorzec = new RegExp(`<(?:${znaczniki.join('|')})(?=[\\s/>])`, 'gi');
  const poczatki: number[] = [];
  for (let m = wzorzec.exec(html); m !== null; m = wzorzec.exec(html)) {
    poczatki.push(koniecZnacznika(html, m.index));
    /** ⚠️ Szukamy dalej OD KOŃCA znacznika, żeby atrybut z `<` nie dawał drugiego trafienia. */
    wzorzec.lastIndex = poczatki[poczatki.length - 1]!;
  }
  if (poczatki.length === 0) return null;
  return poczatki.map((od, i) => html.slice(od, i + 1 < poczatki.length ? poczatki[i + 1]! : html.length));
}

/**
 * Wiersze i komórki z postaci HTML schowka. `null` = to nie jest tabela.
 *
 * ⚠️⚠️ TABELA ZAGNIEŻDŻONA W KOMÓRCE DA NADMIAROWE WIERSZE i tego nie udajemy: bierzemy wszystkie
 * `<tr>`, jakie w napisie są. Nie zgadujemy zagnieżdżenia, bo pomyłka w zgadywaniu ucina wiersze
 * z DANYMI — a nadmiarowy wiersz widać w podglądzie i odznacza się go jednym kliknięciem
 * (mechanizm odznaczania jest w tym samym oknie). Cichy brak człowieka jest gorszy od śmiecia,
 * który widać.
 */
export function wierszeZHtml(html: string): string[][] | null {
  if (!/<t[rdh](?=[\s/>])/i.test(html)) return null;
  const wiersze = wytnij(html, ['tr']);
  if (wiersze === null) return null;

  const out: string[][] = [];
  for (const w of wiersze) {
    const komorki = wytnij(w, ['td', 'th']);
    if (komorki === null) continue;
    const pola = komorki.map(tekstKomorkiHtml);
    /** ⚠️ Wiersz całkowicie pusty odsiewamy — tabele z maila mają wiersze na odstęp. */
    if (pola.some((p) => p !== '')) out.push(pola);
  }
  return out.length === 0 ? null : out;
}

/**
 * NAPRAWA ROZPISANEJ WKLEJKI — gdy postaci HTML nie ma, a `text/plain` jest w kształcie z maila.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ KSZTAŁT PRZEPISANY Z PRAWDZIWEJ WKLEJKI (07.09.2026): treść komórki, potem linijka
 *  Z SAMYM TABULATOREM, potem pusta. Ostatnią komórkę wiersza zamyka pusta linijka BEZ tabulatora
 *  — i to ona rozdziela wiersze.
 *
 *  ⚠️⚠️ TYLKO GDY PODPIS SIĘ ZGADZA, czyli gdy w tekście SĄ linijki z samym tabulatorem. Zwykła
 *  wklejka („karta<TAB>nazwa") ich nie ma i idzie starą drogą nietknięta. Naprawa uruchamiana
 *  „na wszelki wypadek" przestawiałaby pola w danych, które były poprawne.
 *
 *  ⚠️ TO JEST DROGA ZAPASOWA, nie główna: rozkład na linijki robi KLIENT POCZTOWY i u innego
 *  może wyglądać inaczej. Właściwą odpowiedzią jest postać HTML (`wierszeZHtml`) — ta funkcja
 *  ratuje dokładnie ten kształt, który zmierzyliśmy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function wierszeZRozpisanej(tekst: string): string[][] | null {
  const linie = tekst.split(/\r\n|\n|\r/);
  const czyTabulator = (l: string) => l.trim() === '' && l.includes('\t');
  if (!linie.some(czyTabulator)) return null;

  const out: string[][] = [];
  let biezacy: string[] = [];
  let poTabulatorze = false;

  const zamknij = () => {
    if (biezacy.length > 0) out.push(biezacy);
    biezacy = [];
  };

  for (const l of linie) {
    if (czyTabulator(l)) {
      /** Linijka z samym tabulatorem = „następna komórka TEGO wiersza". */
      poTabulatorze = true;
      continue;
    }
    if (l.trim() === '') {
      /**
       * ⚠️ PUSTA PO TABULATORZE NIC NIE ZNACZY — to dopełnienie pary, którą wstawił klient
       * pocztowy. Pusta BEZ tabulatora przed nią kończy wiersz. Bez tego rozróżnienia każda
       * komórka stawałaby się osobnym wierszem, czyli wracalibyśmy do zgłoszonego objawu.
       */
      if (!poTabulatorze) zamknij();
      poTabulatorze = false;
      continue;
    }
    poTabulatorze = false;
    biezacy.push(l.replace(/ /g, ' ').trim());
  }
  zamknij();

  return out.length === 0 ? null : out;
}

/** Wiersze → tekst rozdzielony tabulatorami, czyli postać, którą okno importu już rozumie. */
export function doTsv(wiersze: readonly string[][]): string {
  return wiersze.map((w) => w.join('\t')).join('\n');
}

/**
 * CO WSTAWIĆ DO POLA TEKSTOWEGO. `null` = zostaw zwykłe wklejenie przeglądarki.
 *
 * ⚠️ KOLEJNOŚĆ JEST TREŚCIĄ: najpierw HTML (prawdziwa tabela, to samo źródło co Excel), potem
 * naprawa rozpisanej wklejki, a na końcu — nic, czyli zachowanie domyślne. Odwrotna kolejność
 * kazałaby heurystyce rozstrzygać przypadki, w których mamy pewną odpowiedź.
 */
export function zeSchowka(html: string, tekst: string): string | null {
  const zHtml = html.trim() === '' ? null : wierszeZHtml(html);
  if (zHtml !== null) return doTsv(zHtml);
  const naprawione = wierszeZRozpisanej(tekst);
  return naprawione === null ? null : doTsv(naprawione);
}
